/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import React, {
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react'
import { Alert } from 'antd'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { App } from 'antd'
import { checkIfUserIsVerified } from './New_ConfirmYourEmail'
import { registerError } from 'src/utils/sentry'
import { EventCard } from 'src/components/RegistrationEventCard'
import { EventProgress } from 'src/components/RegistrationEventProgress'
import { RegistrationContext } from '../context/RegistrationContext'
import { Team } from '@sportninja/common/hooks/useUserRegistrationTeams'
import { PageTitle, REGISTRATION_STEPS } from './Main'
import Button from 'src/components/ts-components/Button'
import LoggedUserBar from '../components/LoggedUserBar'
import SelectedDivisionCard from '../../RegistrationPreview/components/SelectedDivisionCard'
import PromoCodeSection from '../components/PromoCodeSection'
import TeamInfoSection from '../components/TeamInfoSection'
import DynamicForm from 'src/components/DynamicForm'
import { getSelectedForm } from '../../RegistrationPreview/utils'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { AnswerAction } from 'src/components/DynamicForm'
import { AnswerState } from '../context/RegistrationContext'
import { mapFormAnswers, mapFormAnswersObject } from 'src/utils/mapFormAnswers'
import { formForSelectedPlayer } from '../utils/existingPlayerForm'
import { mapInvalidFields } from 'src/utils/invalidFieldMapper'
import { usePrefillReservedFields } from 'src/hooks/usePrefillReservedFields'
import { handleFormErrors, scrollToErrorAlert } from 'src/utils/scrollToError'

// Feature flag for new key-value payload format
const USE_DYNAMIC_ANSWERS_OBJECT = true // TODO: Replace with environment variable when ready

/**
 * 🔄 New_CustomRegister: Dynamic registration screen that replaces TeamRegister
 * Provides a streamlined registration experience with reusable components
 */
const New_CustomRegister: React.FC = () => {
  const {
    registration,
    setStep,
    goBack,
    email,
    resetSession,
    isTeamRep,
    division,
    couponInfo,
    setCouponInfo,
    currency,
    currencyCode,
    userTeams,
    selectedTeam,
    setSelectedTeam,
    types,
    fetchTypes,
    refetchUserTeams,
    country,
    stateProvince,
    answers,
    setAnswers,
    userId,
    updateSessionTeamRegisterDynamic,
    updateSessionPlayerRegisterDynamic,
    updateSessionPayment,
    updateSessionWaiver,
    updateSessionConfirmSubmission,
    zipCode,
    deleteSession,
    session,
    teamOfficialType,
    role,
    selectedPlayer,
    birthDate,
  } = useContext(RegistrationContext)

  const { message } = App.useApp()

  // Anchor to ensure we start the view at the beginning of the form (team selector level)
  const teamSelectorTopRef = useRef<HTMLDivElement | null>(null)

  // On first mount, make sure we scroll to the beginning of the form
  useEffect(() => {
    console.log(
      '🔄 New_CustomRegister: Ensuring scroll starts at team selector'
    )
    // Defer to allow initial layout to complete before scrolling
    const tid = window.setTimeout(() => {
      teamSelectorTopRef.current?.scrollIntoView({
        behavior: 'auto',
        block: 'start',
      })
    }, 0)
    return () => window.clearTimeout(tid)
  }, [])

  /**
   * 🔄 shouldStartInAddMode: Helper to determine the correct default mode for team creation
   * This is only used for setting the INITIAL default mode, not for overriding user selections
   * - Players: always false (select only)
   * - Team reps: true only when they have no existing teams
   * - Team reps: if there is a team name in the session, but no team id, we should start in add mode
   */
  const shouldStartInAddMode = useCallback(
    (isTeamRepFlag: boolean, teams: Team[] | null | undefined): boolean => {
      if (!isTeamRepFlag) {
        // Players never create teams
        return false
      }
      // If there is a team name in the session, but no team id, we should start in add mode
      if (session && session?.team_name && !session?.team_id) {
        return true
      }
      // Team reps only create when no teams exist
      return (teams?.length ?? 0) === 0
    },
    [session]
  )

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPromoCode, setShowPromoCode] = useState(!!couponInfo?.id)
  const [isAddingNewTeam, setIsAddingNewTeam] = useState(
    shouldStartInAddMode(isTeamRep, userTeams)
  )
  const [hasSetInitialMode, setHasSetInitialMode] = useState(false)
  const [newTeamName, setNewTeamName] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<string[]>([])
  const [customError, setCustomError] = useState<Record<string, string>>({})

  const showWaiver = registration?.registration?.show_waiver

  /**
   * 🔄 isOneHundredPercentCoupon: Helper to determine if coupon covers full cost
   */
  const isOneHundredPercentCoupon = useMemo(() => {
    const baseCost = isTeamRep ? division?.team_cost : division?.player_cost
    if (couponInfo?.discount === 100 && couponInfo?.type === 'percentage') {
      return true
    }
    if (couponInfo?.discount && baseCost) {
      return couponInfo.discount >= baseCost
    }
    return false
  }, [couponInfo, isTeamRep, division])

  /**
   * 🔄 isCouponCoveringDeposit: Checks if the coupon fully covers the deposit amount
   */
  const isCouponCoveringDeposit = useMemo(() => {
    if (!couponInfo || !couponInfo.applies_to_deposit) {
      return false
    }
    const depositAmount = isTeamRep
      ? division?.deposit
      : division?.player_deposit
    if (!depositAmount || depositAmount <= 0) {
      return false
    }
    if (couponInfo.type === 'percentage' && couponInfo.discount >= 100) {
      return true
    }
    if (
      couponInfo.deposit_discount_amount &&
      couponInfo.deposit_discount_amount >= depositAmount
    ) {
      return true
    }
    if (
      couponInfo.type !== 'percentage' &&
      couponInfo.discount &&
      couponInfo.discount >= depositAmount
    ) {
      return true
    }
    return false
  }, [couponInfo, isTeamRep, division?.deposit, division?.player_deposit])

  // Get selected form based on user role (team rep vs player)
  const selectedForm = useMemo(() => {
    if (!registration?.registration) {
      return null
    }
    const formType = isTeamRep ? 'team' : 'player'
    return getSelectedForm(registration, formType)
  }, [registration, isTeamRep])

  // The relationship field is only relevant when registering a brand-new
  // player. For an existing player it is stripped from both the rendered form
  // and the submission payload (otherwise it would leak in as an empty value).
  const formForPlayer = useMemo(
    () => formForSelectedPlayer(selectedForm, selectedPlayer),
    [selectedForm, selectedPlayer]
  )

  // Handle prefilling reserved fields when selectedPlayer is available
  const { prefilledAnswers, disabledFieldIds } = usePrefillReservedFields(
    selectedForm,
    selectedPlayer,
    isTeamRep,
    answers,
    email,
    birthDate
  )

  /**
   * 🔄 answersReducer: Manages form answers state
   */
  const answersReducer = (
    state: AnswerState,
    action: AnswerAction
  ): AnswerState => {
    switch (action.type) {
      case 'SET':
        return { ...state, [action.id]: action.value }
      case 'SET_ALL':
        return { ...action.payload }
      default:
        return state
    }
  }

  /**
   * 🔄 dispatchAnswer: Wrapper function to translate setAnswers to reducer actions
   */
  const dispatchAnswer = (action: AnswerAction) => {
    console.log('🔄 dispatchAnswer: Dispatching action to context', { action })
    setAnswers((prev) => answersReducer(prev, action))
  }

  // Effect to merge prefilled answers when they change
  useEffect(() => {
    if (Object.keys(prefilledAnswers).length > 0) {
      console.log('🔄 useEffect: Merging prefilled answers', {
        prefilledAnswers,
        currentAnswers: answers,
      })
      setAnswers((prev) => ({ ...prev, ...prefilledAnswers }))
    }
  }, [prefilledAnswers, setAnswers]) // Note: answers is intentionally excluded to prevent infinite loops

  /**
   * Effect to handle team name and team selection based on session data.
   * - If the session has a team name but no team ID, set the new team name and mark as adding new team.
   * - If the session has a team ID, select the corresponding team from userTeams.
   *
   * Safety checks:
   * - Ensures session, userTeams, and their properties are defined before accessing.
   * - Avoids unnecessary state updates if values are already set.
   */
  useEffect(() => {
    if (session && typeof session.team_name === 'string' && !session.team_id) {
      if (newTeamName === null) {
        setNewTeamName(session.team_name)
        setIsAddingNewTeam(true)
      }
    } else if (
      session &&
      typeof session.team_id !== 'undefined' &&
      session.team_id
    ) {
      if (!selectedTeam && Array.isArray(userTeams)) {
        const team = userTeams.find(
          (el) =>
            el && typeof el.uid !== 'undefined' && el.uid === session.team_id
        )
        if (team) {
          setSelectedTeam(team as any)
        }
      }
    }
  }, [newTeamName, selectedTeam, session, userTeams])

  /**
   * 🔄 Data-fetch effects: Ensure types are loaded and user teams are refreshed
   */
  useEffect(() => {
    console.log('🔄 useEffect: Checking types and fetching if needed', {
      types,
    })
    if (!types || Object.keys(types).length === 0) {
      fetchTypes()
    }
  }, [types, fetchTypes])

  useEffect(() => {
    console.log('🔄 useEffect: Refetching user teams')
    refetchUserTeams()
  }, [refetchUserTeams])

  // Effect to set correct default mode when userTeams data loads (only once)
  useEffect(() => {
    // Only set the initial mode if we haven't done so already and userTeams has loaded
    if (!hasSetInitialMode && userTeams !== null && userTeams !== undefined) {
      console.log(
        '🔄 useEffect: Setting initial default mode based on userTeams',
        {
          isTeamRep,
          userTeamsCount: userTeams?.length || 0,
          currentIsAddingNewTeam: isAddingNewTeam,
        }
      )
      const correctMode = shouldStartInAddMode(isTeamRep, userTeams)
      if (correctMode !== isAddingNewTeam) {
        setIsAddingNewTeam(correctMode)
      }
      setHasSetInitialMode(true)
    }
  }, [
    isTeamRep,
    userTeams,
    isAddingNewTeam,
    shouldStartInAddMode,
    hasSetInitialMode,
  ])

  // Reset form when selected form changes (role or division change)
  useEffect(() => {
    console.log(
      '🔄 useEffect: Form definition changed, answers will be reset',
      {
        selectedForm: selectedForm?.id,
      }
    )
    // Form answers are now managed in RegistrationContext and reset automatically when division/role changes
    // Also reset the initial mode flag so it can be recalculated for the new role
    setHasSetInitialMode(false)
  }, [selectedForm])

  /**
   * 🔄 handleSetSelectedTeam: Wrapper to handle null team selection
   */
  const handleSetSelectedTeam = (team: any) => {
    console.log('🔄 handleSetSelectedTeam: Setting selected team', { team })
    if (team) {
      setSelectedTeam(team)
    } else {
      setSelectedTeam(null as any)
    }
  }

  console.log('selectedTeam', selectedTeam)

  /**
   * 🔄 submitRegistration: Handles the registration submission process
   */
  const submitRegistration = useCallback(async () => {
    console.log('🔄 submitRegistration: Starting registration submission')
    setLoading(true)
    setError(null)

    try {
      setCustomError({})
      setFieldErrors([])
      // Early validation: Country/Province requirement
      if (
        !isTeamRep &&
        (country === 'US' || country === 'CA') &&
        !stateProvince
      ) {
        setCustomError((prev) => ({
          ...prev,
          stateProvince: 'State / Province is required',
        }))
        setFieldErrors((prev) => [...new Set([...prev, 'stateProvince'])])
        setLoading(false)
        // Scroll to the state/province error
        handleFormErrors(['stateProvince'], {
          stateProvince: 'State / Province is required',
        })
        return
      }

      const isNewTeamNameEmptyOrNull =
        newTeamName === null || newTeamName === ''

      // Build team information
      const teamId = isNewTeamNameEmptyOrNull
        ? selectedTeam?.uid || (selectedTeam as any)?.id || null
        : null
      const teamName = isNewTeamNameEmptyOrNull
        ? selectedTeam?.name || ''
        : newTeamName || ''

      // Map form answers to required payload format
      if (formForPlayer) {
        const dynamicAnswers = USE_DYNAMIC_ANSWERS_OBJECT
          ? mapFormAnswersObject(formForPlayer, answers)
          : mapFormAnswers(formForPlayer, answers)
        console.log(
          `🔄 submitRegistration: ${
            USE_DYNAMIC_ANSWERS_OBJECT ? 'Key-value' : 'Array-per-section'
          } payload prepared`,
          {
            dynamicAnswers,
            teamId,
            teamName,
            currencyCountryId: currency?.currency_country_id,
            currencyCode: currencyCode,
            currencyAbbreviation: currency?.abbreviation,
            currencyNameFull: currency?.name_full,
          }
        )

        // Call the appropriate dynamic registration function
        if (isTeamRep) {
          await updateSessionTeamRegisterDynamic({
            coupon_code_id: couponInfo?.id || '',
            user_id: userId || '',
            currency_country_id:
              registration?.registration?.currency_country?.id || '',
            country_code: country || '',
            postal_code: zipCode || '',
            team_id: teamId,
            team_name: teamName,
            team_player_roster_id:
              (selectedTeam as any)?.team_player_roster_id || '',
            province: stateProvince || '',
            dynamic_answers: dynamicAnswers,
            existing_team_official_id:
              selectedPlayer && selectedPlayer.id !== 'new'
                ? selectedPlayer.id
                : undefined,
            team_official_id:
              selectedPlayer && selectedPlayer.id !== 'new'
                ? selectedPlayer.id
                : undefined,
          })
        } else {
          await updateSessionPlayerRegisterDynamic({
            team_id: teamId,
            team_name: teamName,
            user_id: userId || '',
            province: stateProvince || '',
            coupon_code_id: couponInfo?.id || '',
            currency_country_id:
              registration?.registration?.currency_country?.id || '',
            team_player_roster_id:
              (selectedTeam as any)?.team_player_roster_id || '',
            dynamic_answers: dynamicAnswers,
            player_id:
              selectedPlayer && selectedPlayer.id !== 'new'
                ? selectedPlayer.id
                : undefined,
            existing_player_id:
              selectedPlayer && selectedPlayer.id !== 'new'
                ? selectedPlayer.id
                : undefined,
          })
        }

        // Handle waiver and payment flow
        if (!showWaiver || isTeamRep) {
          await updateSessionPayment()
        }
      }

      // Determine next step based on waiver and payment requirements
      const depositToCheck = isTeamRep
        ? division?.deposit
        : division?.player_deposit
      const singlePayment = isTeamRep
        ? division?.single_payment
        : division?.player_single_payment

      console.log('🔄 submitRegistration: Determining next step', {
        showWaiver,
        isTeamRep,
        depositToCheck,
        singlePayment,
        isOneHundredPercentCoupon,
      })

      if (showWaiver && !isTeamRep) {
        await updateSessionWaiver(false)
        setStep(REGISTRATION_STEPS.WAIVER)
      } else if (singlePayment || (depositToCheck && depositToCheck > 0)) {
        const shouldSkipPayment =
          (singlePayment && isOneHundredPercentCoupon) ||
          (depositToCheck && depositToCheck > 0 && isCouponCoveringDeposit)

        if (shouldSkipPayment) {
          // Handle 100% coupon (single payment or deposit) - skip payment and confirm
          checkIfUserIsVerified()
            .then(() => {
              updateSessionConfirmSubmission(false)
                .then(() => {
                  setStep(REGISTRATION_STEPS.REGISTER_REVIEW)
                })
                .catch((e) => {
                  message.open({
                    type: 'error',
                    content:
                      'We were unable to confirm your submission. Please Contact SportNinja Support.',
                    duration: 6,
                  })
                  registerError(e, 'Error confirming submission', {
                    session: session,
                    division: division,
                    couponInfo: couponInfo,
                    teamOfficialType: teamOfficialType,
                    role: role,
                  })
                })
            })
            .catch((_e) => {
              setTimeout(() => {
                setStep(REGISTRATION_STEPS.CONFIRM_YOUR_EMAIL)
              }, 2000)
            })
        } else {
          setStep(REGISTRATION_STEPS.REGISTRATION_PAYMENT)
        }
      } else {
        // Free registration - verify email and confirm submission
        checkIfUserIsVerified()
          .then(() => {
            updateSessionConfirmSubmission()
              .then(() => {
                setStep(REGISTRATION_STEPS.REGISTER_REVIEW)
              })
              .catch((e) => {
                message.open({
                  type: 'error',
                  content:
                    'We were unable to confirm your submission. Please Contact SportNinja Support.',
                  duration: 6,
                })
                registerError(e, 'Error confirming submission', {
                  session: session,
                  division: division,
                  couponInfo: couponInfo,
                  teamOfficialType: teamOfficialType,
                  role: role,
                })
              })
          })
          .catch((_e) => {
            setTimeout(() => {
              setStep(REGISTRATION_STEPS.CONFIRM_YOUR_EMAIL)
            }, 2000)
          })
      }
    } catch (err: any) {
      console.error('❌ submitRegistration: Error during submission', err)

      if (err?.status === 410) {
        deleteSession()
        window.location.reload()
        return
      }

      if (err?.invalid_fields?.coupon_code) {
        message.open({
          type: 'error',
          content: err.invalid_fields.coupon_code,
        })
        // Scroll to error alert if present
        setTimeout(() => scrollToErrorAlert(), 200)
        return
      }

      // Handle array-style errors
      if (
        Array.isArray(err?.invalid_fields) &&
        err?.invalid_fields?.length > 0
      ) {
        message.open({
          type: 'error',
          content: err.invalid_fields[0],
        })
        // Scroll to error alert
        setTimeout(() => scrollToErrorAlert(), 200)
        return
      }
      if (err?.invalid_fields) {
        const { fieldErrors, customError } = mapInvalidFields(
          err.invalid_fields
        )
        setFieldErrors(fieldErrors)
        setCustomError(customError)
        // Trigger scroll to first error field
        handleFormErrors(fieldErrors, customError)
        return
      }

      const errorMessages = getErrorMessage(err)
      message.open({
        type: 'error',
        content: errorMessages,
      })
      // Scroll to error alert for general errors
      setTimeout(() => scrollToErrorAlert(), 200)
    } finally {
      setLoading(false)
    }
  }, [
    answers,
    country,
    couponInfo,
    currency?.abbreviation,
    currencyCode,
    currency?.name_full,
    deleteSession,
    division,
    registration,
    isCouponCoveringDeposit,
    isOneHundredPercentCoupon,
    isTeamRep,
    message,
    newTeamName,
    role,
    selectedForm,
    formForPlayer,
    selectedTeam,
    session,
    setStep,
    showWaiver,
    stateProvince,
    teamOfficialType,
    updateSessionConfirmSubmission,
    updateSessionPayment,
    updateSessionPlayerRegisterDynamic,
    updateSessionTeamRegisterDynamic,
    updateSessionWaiver,
    userId,
    zipCode,
    selectedPlayer,
  ])

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        flex: 1;
        justify-content: center;
        background-color: ${colors.NEUTRAL_800};
        width: 1080px;
        padding: 0 35px 35px 35px;
        border-radius: 16px 16px 0px 0px;
        @media (max-width: 768px) {
          min-width: 0;
          width: 100%;
        }
      `}
    >
      <LoggedUserBar
        email={email}
        onStartOver={() => {
          resetSession()
          setTimeout(() => {
            window.location.reload()
          }, 500)
        }}
      />

      <EventCard eventInfo={registration as any} />

      <EventProgress
        eventProgress={3}
        isWaiverRequired={showWaiver}
        isTeamRep={isTeamRep}
      />

      {/* Anchor near the page alert ("Do Not Close...") to be initial focus */}
      <div ref={teamSelectorTopRef} />
      <PageTitle
        title={isTeamRep ? 'Register Your Team' : 'Register Player'}
        subtitle='Provide the necessary details to proceed with your registration.'
        showAlert={true}
        showSuccess={false}
      />

      {/* Selected Division Card */}
      {division && (
        <SelectedDivisionCard
          division={division}
          couponInfo={couponInfo}
          isTeamRep={isTeamRep}
          colors={colors}
          currency={currencyCode ? { currency_code: currencyCode } : undefined}
          onChangeDivision={() => {
            setCouponInfo(null)
            setSelectedTeam(null as any)
            setNewTeamName('')
            setIsAddingNewTeam(shouldStartInAddMode(isTeamRep, userTeams))
            setHasSetInitialMode(false)
            setStep(REGISTRATION_STEPS.SELECT_YOUR_DIVISION)
          }}
          onTogglePromoCode={() => {
            setShowPromoCode(!showPromoCode)
            if (showPromoCode === false) {
              // Reset promo code state when hiding
            }
          }}
          showPromoCode={showPromoCode}
          isCanlan={isCanlan}
        />
      )}

      {/* Promo Code Section */}
      {showPromoCode && division && (
        <PromoCodeSection
          couponInfo={couponInfo}
          setCouponInfo={setCouponInfo}
          division={division}
          isTeamRep={isTeamRep}
        />
      )}

      {/* Team Info Section */}
      <TeamInfoSection
        isTeamRep={isTeamRep}
        userTeams={userTeams}
        showExistingRegistrationsPlayers={
          division?.show_existing_registrations_players || false
        }
        selectedTeam={selectedTeam}
        setSelectedTeam={handleSetSelectedTeam}
        isAddingNewTeam={isAddingNewTeam}
        setIsAddingNewTeam={setIsAddingNewTeam}
        newTeamName={newTeamName || ''}
        setNewTeamName={setNewTeamName}
        fieldErrors={fieldErrors}
        customError={customError}
        isCanlan={isCanlan}
      />

      {/* Dynamic Form Section */}
      {selectedForm ? (
        <DynamicForm
          groups={formForPlayer?.groups ?? []}
          sportId={
            registration?.registration?.sport_id
              ? registration.registration.sport_id
              : undefined
          }
          onSubmit={submitRegistration}
          onBack={() => {
            goBack(REGISTRATION_STEPS.SELECT_YOUR_ROLE)
          }}
          loading={loading}
          backButtonText='Back'
          nextButtonText='Next'
          answers={answers}
          dispatchAnswer={dispatchAnswer}
          fieldErrors={fieldErrors}
          customError={customError}
          disabledFields={disabledFieldIds}
          scheduleId={registration?.registration?.schedule_id}
        />
      ) : (
        <Alert
          type='warning'
          message='No dynamic form is configured for this role.'
          showIcon
          css={css`
            margin-bottom: 16px;
          `}
        />
      )}

      {error && !selectedForm && (
        <div
          id='form-error-alert'
          css={css`
            margin-bottom: 16px;
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            max-width: 500px;
            align-self: center;
          `}
        >
          <Alert message={error} type='error' showIcon />
        </div>
      )}

      {/* Navigation Buttons - Only show when no form is available */}
      {!selectedForm && (
        <div
          css={css`
            display: flex;
            flex-direction: row;
            justify-content: flex-end;
            gap: 8px;
            align-self: flex-end;
            width: 50%;
            @media (max-width: 768px) {
              flex-direction: column-reverse;
              width: 100%;
            }
          `}
        >
          <Button
            variant='secondary'
            text='Back'
            disabled={loading}
            onClick={() => {
              goBack(REGISTRATION_STEPS.SELECT_YOUR_ROLE)
            }}
          />
          <Button
            variant='primary'
            text='Next'
            disabled={loading}
            onClick={() => submitRegistration()}
          />
        </div>
      )}
    </div>
  )
}

export default New_CustomRegister
